<template>
    <Primitive
        :as="as"
        :class="classes"
        :aria-describedby="ownDescribedBy"
        v-bind="rootAttrs"
    >
        <component
            :is="isNativeFieldset ? 'legend' : 'div'"
            v-if="hasLegend"
            :id="legendId"
            :class="[element('legend'), { 'k-visually-hidden': hideLegend }]"
        >
            <slot name="legend">
                {{ legend }}
            </slot>
            <span
                v-if="required"
                :class="element('required')"
                aria-hidden="true"
            >*</span>
        </component>

        <p
            v-if="hasDescription"
            :id="descriptionId"
            :class="element('description')"
        >
            <slot name="description">
                {{ description }}
            </slot>
        </p>

        <div :class="element('content')">
            <slot />
        </div>

        <div
            :class="element('messages')"
            aria-live="polite"
        >
            <div
                v-if="hasErrorContent"
                :id="errorId"
                :class="element('error')"
            >
                <slot name="error">
                    <p
                        v-for="(message, index) in errorMessages"
                        :key="index"
                    >
                        {{ message }}
                    </p>
                </slot>
            </div>
        </div>
    </Primitive>
</template>

<script setup lang="ts">
import type { KFieldsetProps } from './types'
import useBem from '@yeeroen/use-bem'
import { Primitive } from 'reka-ui'

import { computed, inject, provide, useId, useSlots } from 'vue'
import { K_FIELDSET_CONTEXT } from './context'

defineOptions({ name: 'KFieldset' })

const props = withDefaults(defineProps<KFieldsetProps>(), {
    as: 'fieldset',
    orientation: 'stacked',
    hideLegend: false,
    describeChildren: false,
    disabled: undefined,
    required: undefined,
})

defineSlots<{
    default?: () => unknown
    legend?: () => unknown
    description?: () => unknown
    error?: () => unknown
}>()

const slots = useSlots()
const parentFieldset = inject(K_FIELDSET_CONTEXT, null)
const generatedId = useId()
const { block, element, modifier } = useBem('k-fieldset')

const isNativeFieldset = computed(() => props.as === 'fieldset')
const hasLegend = computed(() => Boolean(slots.legend || props.legend))
const legendId = computed(() => `${generatedId}-legend`)
const descriptionId = computed(() => `${generatedId}-description`)
const errorId = computed(() => `${generatedId}-error`)
const hasDescription = computed(() => Boolean(slots.description || props.description))
const errorMessages = computed(() => {
    if (typeof props.error === 'string') {
        return props.error ? [props.error] : []
    }

    return Array.isArray(props.error) ? props.error.filter(Boolean) : []
})
const invalid = computed(() => Boolean(props.error)
    && !(Array.isArray(props.error) && props.error.length === 0))
const hasErrorContent = computed(() => invalid.value
    && Boolean(slots.error || errorMessages.value.length))
const disabled = computed(() => parentFieldset?.nativeDisabled.value
    || (props.disabled ?? parentFieldset?.disabled.value ?? false))
const nativeDisabled = computed(() => parentFieldset?.nativeDisabled.value
    || (isNativeFieldset.value && disabled.value))
const required = computed(() => props.required ?? parentFieldset?.required.value ?? false)
const size = computed(() => props.size ?? parentFieldset?.size.value ?? 'md')
const ownDescribedBy = computed(() => [
    hasDescription.value ? descriptionId.value : undefined,
    hasErrorContent.value ? errorId.value : undefined,
].filter(Boolean).join(' ') || undefined)
const childDescribedBy = computed(() => [
    parentFieldset?.describedBy.value,
    props.describeChildren ? ownDescribedBy.value : undefined,
].filter(Boolean).join(' ') || undefined)
const classes = computed(() => block(
    modifier(props.orientation),
    modifier('invalid', invalid.value),
    modifier('disabled', disabled.value),
))
const rootAttrs = computed(() => isNativeFieldset.value
    ? { disabled: disabled.value || undefined }
    : {
            'role': 'group',
            'aria-labelledby': hasLegend.value ? legendId.value : undefined,
            'aria-disabled': disabled.value || undefined,
        })

provide(K_FIELDSET_CONTEXT, {
    disabled,
    nativeDisabled,
    required,
    size,
    describedBy: childDescribedBy,
})
</script>

<style lang="scss">
@use './k-fieldset.scss';
</style>
